import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { entrevistasApi } from '../api/procesos'
import { colors, text, space, sx } from '../design/tokens'
import Boton from '../components/Boton'
import Modal from '../components/Modal'

// Relevamiento de procesos con IA — listado de entrevistas (solo super_admin).
// Cada entrevista genera un link público por token que se le pasa al empleado.

export const ESTADO_ENTREVISTA = {
  pendiente:  { label: 'Pendiente',  bg: colors.bgSoft,  color: colors.textSec },
  en_curso:   { label: 'En curso',   bg: colors.infoBg,  color: colors.info },
  completada: { label: 'Completada', bg: colors.okBg,    color: colors.ok },
  analizada:  { label: 'Analizada',  bg: colors.t80Bg,   color: colors.t80Text },
}

export function BadgeEntrevista({ estado }) {
  const e = ESTADO_ENTREVISTA[estado] || ESTADO_ENTREVISTA.pendiente
  return <span style={{ ...sx.badge, background: e.bg, color: e.color }}>{e.label}</span>
}

export function linkEntrevista(token) {
  return `${window.location.origin}/entrevista/${token}`
}

function fmtFecha(f) {
  if (!f) return '—'
  const d = new Date(f)
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export default function ProcesosEntrevistasPage() {
  const navigate = useNavigate()
  const [entrevistas, setEntrevistas] = useState([])
  const [cargando, setCargando] = useState(true)
  const [modalAlta, setModalAlta] = useState(false)
  const [form, setForm] = useState({ nombre: '', area: '', puesto: '' })
  const [guardando, setGuardando] = useState(false)
  const [copiado, setCopiado] = useState(null)

  const cargar = useCallback(() => {
    entrevistasApi.listar()
      .then(({ data }) => setEntrevistas(data))
      .finally(() => setCargando(false))
  }, [])

  useEffect(() => { cargar() }, [cargar])

  const crear = async (e) => {
    e.preventDefault()
    setGuardando(true)
    try {
      await entrevistasApi.crear(form)
      setModalAlta(false)
      setForm({ nombre: '', area: '', puesto: '' })
      cargar()
    } finally {
      setGuardando(false)
    }
  }

  const copiarLink = (ent) => {
    navigator.clipboard.writeText(linkEntrevista(ent.token))
    setCopiado(ent.id)
    setTimeout(() => setCopiado(null), 1500)
  }

  return (
    <div>
      <div style={sx.toolbar}>
        <h1 style={{ margin: 0, fontSize: 20 }}>Relevamiento IA</h1>
        <span style={{ fontSize: text.sec, color: colors.textSec }}>
          Entrevistas sobre cómo trabaja cada empleado — para detectar qué automatizar
        </span>
        <div style={{ marginLeft: 'auto' }}>
          <Boton variante="primary" onClick={() => setModalAlta(true)}>+ Nueva entrevista</Boton>
        </div>
      </div>

      {cargando ? (
        <p style={{ color: colors.textSec }}>Cargando…</p>
      ) : entrevistas.length === 0 ? (
        <div style={{ ...sx.card, textAlign: 'center', color: colors.textSec, padding: 40 }}>
          Todavía no hay entrevistas. Creá la primera y pasale el link al empleado por WhatsApp.
        </div>
      ) : (
        <div style={{ ...sx.card, padding: 0, overflow: 'hidden' }}>
          <table style={sx.table}>
            <thead>
              <tr>
                <th style={sx.th}>Empleado</th>
                <th style={sx.th}>Estado</th>
                <th style={{ ...sx.th, textAlign: 'right' }}>Tareas</th>
                <th style={sx.th}>Creada</th>
                <th style={sx.th}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {entrevistas.map(ent => (
                <tr
                  key={ent.id}
                  onClick={() => navigate(`/procesos/entrevistas/${ent.id}`)}
                  style={{ borderBottom: `1px solid ${colors.borderSoft}`, height: 48, cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                  title="Ver entrevista"
                >
                  <td style={sx.td}>
                    <div style={{ fontWeight: 600 }}>{ent.nombre}</div>
                    {(ent.area || ent.puesto) && (
                      <div style={{ fontSize: text.sec, color: colors.textMeta }}>
                        {[ent.area, ent.puesto].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </td>
                  <td style={sx.td}><BadgeEntrevista estado={ent.estado} /></td>
                  <td style={{ ...sx.td, ...sx.mono, textAlign: 'right' }}>{ent.tareas_count}</td>
                  <td style={{ ...sx.td, ...sx.mono, color: colors.textSec }}>{fmtFecha(ent.created_at)}</td>
                  <td style={sx.td} onClick={e => e.stopPropagation()}>
                    <Boton variante="send" sm onClick={() => copiarLink(ent)}>
                      {copiado === ent.id ? '✓ Copiado' : '🔗 Copiar link'}
                    </Boton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalAlta && (
        <Modal title="Nueva entrevista" onClose={() => setModalAlta(false)}>
          <form onSubmit={crear} style={{ display: 'flex', flexDirection: 'column', gap: space.lg }}>
            <div>
              <label style={sx.cardTitle}>Nombre del empleado *</label>
              <input
                style={{ ...sx.input, width: '100%', boxSizing: 'border-box', marginTop: 4 }}
                value={form.nombre}
                onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                placeholder="Ej: Juan Pérez"
                required
                autoFocus
              />
            </div>
            <div>
              <label style={sx.cardTitle}>Área</label>
              <input
                style={{ ...sx.input, width: '100%', boxSizing: 'border-box', marginTop: 4 }}
                value={form.area}
                onChange={e => setForm(f => ({ ...f, area: e.target.value }))}
                placeholder="Ej: Depósito / Administración / Ventas"
              />
            </div>
            <div>
              <label style={sx.cardTitle}>Puesto</label>
              <input
                style={{ ...sx.input, width: '100%', boxSizing: 'border-box', marginTop: 4 }}
                value={form.puesto}
                onChange={e => setForm(f => ({ ...f, puesto: e.target.value }))}
                placeholder="Ej: Encargado de expedición"
              />
            </div>
            <div style={{ display: 'flex', gap: space.md, justifyContent: 'flex-end' }}>
              <Boton variante="ghost" type="button" onClick={() => setModalAlta(false)}>Cancelar</Boton>
              <Boton variante="primary" type="submit" disabled={guardando || !form.nombre.trim()}>
                {guardando ? 'Creando…' : 'Crear entrevista'}
              </Boton>
            </div>
          </form>
        </Modal>
      )}
    </div>
  )
}
